// AI status banner: polls /api/ai-status every 60s and shows a discreet
// warning strip if image generation is temporarily degraded (rate-limited /
// budget exhausted). The story flow still works — only illustrations are
// paused. Rendered inline in GameView, just under the HUD.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { ImageOff, AlertTriangle } from "lucide-react";
import { t } from "@/utils/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 60_000;

export default function AIStatusBanner({ lang = "en" }) {
  const [status, setStatus] = useState(null); // {images_ok, retry_after_seconds, reason}

  useEffect(() => {
    let mounted = true;
    let timer;

    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/ai-status`, { timeout: 8000 });
        if (!mounted) return;
        setStatus(res.data);
      } catch {
        // Silent: banner just won't show if the endpoint is unreachable
      }
    };

    fetchStatus();
    timer = setInterval(fetchStatus, POLL_INTERVAL_MS);

    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);

  if (!status || status.images_ok) return null;

  const retryMin = Math.max(1, Math.ceil((status.retry_after_seconds || 0) / 60));

  return (
    <div
      className="bg-[#3a2a0a]/90 border-y border-[#cfb53b]/40 px-3 py-2 flex items-start gap-2.5 backdrop-blur-sm"
      data-testid="ai-status-banner"
      role="status"
      aria-live="polite"
    >
      <div className="relative shrink-0 mt-0.5">
        <ImageOff size={16} className="text-[#cfb53b]" />
        <AlertTriangle size={10} className="absolute -bottom-1 -right-1 text-red-400 bg-[#3a2a0a] rounded-full" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] sm:text-xs font-heading text-[#cfb53b] leading-tight">
          {t(lang, "images_degraded_title")}
        </p>
        <p className="text-[10px] sm:text-[11px] text-[#cbd5e1]/90 leading-snug mt-0.5">
          {t(lang, "images_degraded_desc")}
          {status.retry_after_seconds > 0 && (
            <span className="ml-1 text-[#94a3b8]">
              ({t(lang, "images_retry_in")} ~{retryMin} {t(lang, "images_minutes")})
            </span>
          )}
        </p>
      </div>
    </div>
  );
}
